import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useAnalyticsPageView, initAnalytics, useContactLinkTracking } from "@/lib/analytics";
import { useMetaPixelPageView, initMetaPixel } from "@/lib/metaPixel";

initAnalytics();
initMetaPixel();

// Lazy load pages for code splitting
const HomePage = lazy(() => import("./pages/website/HomePage"));
const DienstenPage = lazy(() => import("./pages/website/DienstenPage"));
const ServiceDetailPage = lazy(() => import("./pages/website/ServiceDetailPage"));
const WaaromTavernePage = lazy(() => import("./pages/website/WaaromTavernePage"));
const OverOnsPage = lazy(() => import("./pages/website/OverOnsPage"));
const ContactPage = lazy(() => import("./pages/website/ContactPage"));
const BlogPage = lazy(() => import("./pages/website/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/website/BlogPostPage"));
const FaqPage = lazy(() => import("./pages/website/FaqPage"));
const FulfilmentUitbestedenPage = lazy(() => import("./pages/website/FulfilmentUitbestedenPage"));
const FulfilmentKostenPage = lazy(() => import("./pages/website/FulfilmentKostenPage"));
const FulfilmentOverijsselPage = lazy(() => import("./pages/website/FulfilmentOverijsselPage"));
const FulfilmentNederlandPage = lazy(() => import("./pages/website/FulfilmentNederlandPage"));
const FulfilmentVergelijkenPage = lazy(() => import("./pages/website/FulfilmentVergelijkenPage"));
const SectorFulfilmentPage = lazy(() => import("./pages/website/SectorFulfilmentPage"));
const TopicFulfilmentPage = lazy(() => import("./pages/website/SectorFulfilmentPage"));
const CityFulfilmentRoute = lazy(() => import("./pages/website/CityFulfilmentRoute"));
const AlgemeneVoorwaardenPage = lazy(() => import("./pages/website/AlgemeneVoorwaardenPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/website/PrivacyPolicyPage"));
const ProtectedDashboard = lazy(() => import("./pages/ProtectedDashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsPageViewTracker />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>}>
            <Routes>
              {/* Public website routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/diensten" element={<DienstenPage />} />
              <Route path="/diensten/:slug" element={<ServiceDetailPage />} />
              <Route path="/waarom-taverne" element={<WaaromTavernePage />} />
              <Route path="/over-ons" element={<OverOnsPage />} />
              <Route path="/fulfilment-uitbesteden" element={<FulfilmentUitbestedenPage />} />
              <Route path="/fulfilment-kosten" element={<FulfilmentKostenPage />} />
              <Route path="/fulfilment-overijssel" element={<FulfilmentOverijsselPage />} />
              <Route path="/fulfilment-nederland" element={<FulfilmentNederlandPage />} />
              <Route path="/fulfilment-vergelijken" element={<FulfilmentVergelijkenPage />} />
              {/* Branchepagina's: /fulfilment-food, /fulfilment-cosmetica, ... */}
              <Route path="/fulfilment-food" element={<SectorFulfilmentPage />} />
              <Route path="/fulfilment-cosmetica" element={<SectorFulfilmentPage />} />
              <Route path="/fulfilment-supplementen" element={<SectorFulfilmentPage />} />
              <Route path="/fulfilment-kleding" element={<SectorFulfilmentPage />} />
              <Route path="/fulfilment-non-food" element={<SectorFulfilmentPage />} />
              {/* Onderwerppagina's rond zoektermen */}
              <Route path="/webshop-fulfilment" element={<TopicFulfilmentPage />} />
              <Route path="/e-fulfilment" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-kleine-webshop" element={<TopicFulfilmentPage />} />
              <Route path="/webshop-logistiek-uitbesteden" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-bedrijf" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-center" element={<TopicFulfilmentPage />} />
              <Route path="/mkb-fulfilment" element={<TopicFulfilmentPage />} />
              <Route path="/ecommerce-fulfilment" element={<TopicFulfilmentPage />} />
              <Route path="/pick-en-pack-uitbesteden" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-partner" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-prijzen" element={<TopicFulfilmentPage />} />
              <Route path="/magazijn-uitbesteden" element={<TopicFulfilmentPage />} />
              <Route path="/orderverwerking-uitbesteden" element={<TopicFulfilmentPage />} />
              <Route path="/retourverwerking-uitbesteden" element={<TopicFulfilmentPage />} />
              <Route path="/webshop-logistiek" element={<TopicFulfilmentPage />} />
              <Route path="/voorraadbeheer-uitbesteden" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-services" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-bedrijven" element={<TopicFulfilmentPage />} />
              <Route path="/opslag-uitbesteden" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-beginnende-webshops" element={<TopicFulfilmentPage />} />
              <Route path="/duurzame-fulfilment" element={<TopicFulfilmentPage />} />
              <Route path="/kleding-webshop-logistiek-uitbesteden" element={<TopicFulfilmentPage />} />
              <Route path="/dropshipping-fulfilment" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-shopify" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-woocommerce" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-lightspeed" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-magento" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-bol" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-groeiende-webshops" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-grote-webshops" element={<TopicFulfilmentPage />} />
              <Route path="/b2b-fulfilment" element={<TopicFulfilmentPage />} />
              <Route path="/omnichannel-fulfilment" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-voor-merken" element={<TopicFulfilmentPage />} />
              <Route path="/overstappen-fulfilmentpartner" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-piekdrukte" element={<TopicFulfilmentPage />} />
              <Route path="/fulfilment-scale-ups" element={<TopicFulfilmentPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/algemene-voorwaarden" element={<AlgemeneVoorwaardenPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              
              {/* Auth routes */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/login" element={<Auth />} />
              
              {/* Dashboard routes (protected) */}
              <Route path="/dashboard" element={<ProtectedDashboard />} />
              <Route path="/admin" element={<Admin />} />
              
              {/* Dynamische stadspagina's: /fulfilment-<stad> */}
              <Route path="/:citySlug" element={<CityFulfilmentRoute />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

const AnalyticsPageViewTracker = () => {
  useAnalyticsPageView();
  useContactLinkTracking();
  useMetaPixelPageView();
  return null;
};

export default App;
